/**
 * Copyright 2013-present, Novivia, Inc.
 * All rights reserved.
 */

/**
 * A module regrouping logic utilities pertaining to bidding in an auction.
 * @module utilities/logic/bidding
 */
"use strict";

var _ = require("lodash");

var FunctionCallError = require("../errors").FunctionCallError;
var InvalidParameterError = FunctionCallError.InvalidParameterError;

/**
 * Compute the next asking price based on the current bid and the incremements.
 * @param  {number}  bidAmount   Amount of the current bid.
 * @param  {array}   increments  Current auction increments.
 * @return {number}  Next asking price.
 */
function computeAskingPrice(bidAmount, increments) {
  if (!_.isNumber(bidAmount)) {
    throw new InvalidParameterError(null, "Invalid amount to use for computing the next asking price.", "bidAmount");
  }

  if (!_.isArray(increments) || _.isEmpty(increments)) {
    throw new InvalidParameterError(null, "Invalid increments to use for computing the next asking price.", "increments");
  }

  for (var i = 0; i < increments.length; i++) {
    var jump = increments[i].jump;
    var start = increments[i].start;
    var nextIncrement = i + 1 < increments.length && increments[i + 1];

    if (nextIncrement) {
      var nextStart = nextIncrement.start;

      if (bidAmount < nextStart) {
        return priceFromAmount(bidAmount, start, jump, nextStart);
      }
    } else {
      return priceFromAmount(bidAmount, start, jump);
    }
  }
}

/**
 * Compute the next closest "legal" bid based on the amount given and up to a
 * specified limit. This allows us to not "bust" the next bracket. If no limit
 * is specified, we presume there is none.
 * @param  {number}  amount   Amount to go above.
 * @param  {number}  start    Amount at the start of the current bracket.
 * @param  {number}  jump     Expected jump for the next bid.
 * @param  {number}  [limit]  Amount to not exceed.
 * @return {number}           Next expected bid, between ]amount, limit].
 */
function priceFromAmount(amount, start, jump, limit) {
  var offset = amount - start;
  var remainder = offset % jump;
  var tentative = amount;

  if (remainder !== 0) {
    tentative = start + Math.floor(offset / jump) * jump;
  }

  tentative += jump;

  // Make sure we don't exceed the limit if one was specified.
  if (limit && tentative >= limit) {
    return limit;
  }

  return tentative;
}

/**
 * Validates whether a bid is valid given the current asking price and the
 * increments.
 * @param  {number}  bidAmount       Amount of the bid to validate.
 * @param  {number}  askingPrice     Current asking price.
 * @param  {array}   increments      Current auction increments.
 * @return {boolean}                 Whether the bid is valid.
 */
function validateBid(bidAmount, askingPrice, increments) {
  if (!_.isNumber(bidAmount)) {
    throw new InvalidParameterError(null, "Invalid bid amount to validate.", "bidAmount");
  }

  if (!_.isNumber(askingPrice)) {
    throw new InvalidParameterError(null, "Invalid current asking price.", "currentAmount");
  }

  if (!_.isArray(increments)) {
    throw new InvalidParameterError(null, "Invalid auction increments.", "increments");
  }

  // Trivial cases.
  if (bidAmount === askingPrice) {
    return true;
  }

  if (bidAmount < askingPrice) {
    return false;
  }

  var minimumBidAmount = computeAskingPrice(askingPrice, increments);

  if (bidAmount === minimumBidAmount) {
    return true;
  }

  // If the bid amount is below the smallest bracket we expected, it's not valid
  // either.
  if (bidAmount < minimumBidAmount) {
    return false;
  }

  // It could be any other higher bid, however, we need to validate if it aligns
  // with the increments.
  for (var i = 0; i < increments.length; i++) {
    var nextIncrement = i + 1 < increments.length && increments[i + 1];
    if (nextIncrement && bidAmount >= nextIncrement.start) {
      continue;
    }

    var incrementStart = increments[i].start;
    var jump = increments[i].jump;
    var remainder = (bidAmount - incrementStart) % jump;

    return remainder === 0;
  }

  return false;
}

module.exports = {
  computeAskingPrice: computeAskingPrice,
  validateBid: validateBid
};


//////////////////
// WEBPACK FOOTER
// /private/var/folders/n9/yczdnstj3wdc9q5tzm6d45c00000gp/T/maison-encans-auex-website/~/@auex/utilities/lib/logic/bidding.js
// module id = 876
// module chunks = 6